import { IS_MATCH_INFO_SHOW_COUNTRY_FLAG } from '@/components/tournamentCenter/config';
import type { ITeam } from '@/interface/IMatch';
import type { ITournamentMatch } from '@/interface/ITournamentMatch';
import { EventStatus, TimezoneOffset, TournamentStage } from '@/interface/enum';
import type { IGroup, IKnockoutMatch, IKnockoutStage, ISboMatch, ITournamentCenterStageConfig } from '@/interface/tournamentCenter';
import { getTeamImage } from '@/libraries/helpers/imageHelper';
import type { ImagePath } from '@/libraries/helpers/imageHelper';
import { formatTranslation } from '@/libraries/oddsApi/oddsApiFormatter';
import { getTimeWithGMT, parseDate, parseISOString } from '@/libraries/utils/date';
import { jsonParse } from '@/stores/helpers/featureParsers';
import type { GroupType, KnockoutType, SboMatchType } from '@/types';

export const flagRoundedImageGlob = import.meta.glob('@/assets/images/sports-country-worldCup/flag-rounded/*.{png,webp}', {
    eager: true,
});

export const flagRectangleImageGlob = import.meta.glob('@/assets/images/sports-country-worldCup/flag-rectangle/*.{png,webp}', {
    eager: true,
});

export const footballPlayerImageGlob = import.meta.glob('@/assets/images/sports-country-worldCup/footballPlayer/*.{png,webp}', {
    eager: true,
});

export const playerAvatarImageGlob = import.meta.glob('@/assets/images/tournament-center/worldCup-element/player-avatar/*.{png,webp}', {
    eager: true,
});

export function convertTournamentGroup(tournamentGroup: GroupType): IGroup {
    return {
        groupName: tournamentGroup.groupName,
        teamList: tournamentGroup.teamList.map(team => ({
            id: team.id,
            name: team.teamName.map(formatTranslation),
            played: team.played,
            won: team.won,
            drawn: team.drawn,
            lost: team.lost,
            goalDifference: team.goalDifference,
            points: team.points,
            ranking: team.ranking,
            isQualified: team.isQualified,
            countryName: team.countryName ?? '',
        })),
    };
}

export function convertTournamentKnockout(knockout: KnockoutType): IKnockoutStage {
    return {
        stage: TournamentStage.fromOddsApi(knockout.stage),
        matchList: knockout.matchList.map(match => ({
            datetime: match.datetime ? getTimeWithGMT(parseISOString(match.datetime), TimezoneOffset.Singapore) : null,
            regularMatch: convertSboMatch(match.regularMatch),
            extraTimeMatch: convertSboMatch(match.extraTimeMatch),
            penaltyMatch: convertSboMatch(match.penaltyMatch),
            homeTeam: match.homeTeam ? {
                id: match.homeTeam.id ?? 0,
                teamName: match.homeTeam.teamName.map(formatTranslation),
                countryName: match.homeTeam.countryName ?? '',
            } : null,
            awayTeam: match.awayTeam ? {
                id: match.awayTeam.id ?? 0,
                teamName: match.awayTeam.teamName.map(formatTranslation),
                countryName: match.awayTeam.countryName ?? '',
            } : null,
            homePlaceHolder: match.homePlaceHolder ?? null,
            awayPlaceHolder: match.awayPlaceHolder ?? null,
        })),
    };
}

export function formatTournamentCenterStageStartDateByLeagueConfig(value?: string): Map<number, Map<TournamentStage, Date>> {
    try {
        const config = jsonParse<ITournamentCenterStageConfig>(value) ?? {};

        const result = new Map<number, Map<TournamentStage, Date>>();
        if (!config) return result;

        Object.entries(config).forEach(([leagueIdStr, stageDates]) => {
            const leagueId = toNumber(leagueIdStr, 0);
            if (leagueId === 0) return;

            const stageMap = new Map<TournamentStage, Date>();

            Object.entries(stageDates).forEach(([stageKey, dateString]) => {
                const stage = toNumber(stageKey, TournamentStage.Unknown);
                if (stage === TournamentStage.Unknown) return;

                const startDate = parseDate(dateString, "yyyy-MM-dd'T'HH:mm:ss", TimezoneOffset.Singapore);
                stageMap.set(stage, startDate);
            });

            if (stageMap.size > 0) {
                result.set(leagueId, stageMap);
            }
        });

        return result;
    } catch (error) {
        console.error('Failed to format tournament center stage start date by league config:', error);
        return new Map();
    }
}

export function convertSboMatch(sboMatch?: SboMatchType | null): ISboMatch | null {
    if (!sboMatch) {
        return null;
    }

    return {
        id: sboMatch.id ?? 0,
        homeTeam: sboMatch.homeTeam ? {
            id: sboMatch.homeTeam.id ?? 0,
            teamName: sboMatch.homeTeam.teamName.map(formatTranslation),
            countryName: sboMatch.homeTeam.countryName ?? '',
        } : null,
        awayTeam: sboMatch.awayTeam ? {
            id: sboMatch.awayTeam.id ?? 0,
            teamName: sboMatch.awayTeam.teamName.map(formatTranslation),
            countryName: sboMatch.awayTeam.countryName ?? '',
        } : null,
        finalScore: sboMatch.finalScore ? {
            home: sboMatch.finalScore.home ?? 0,
            away: sboMatch.finalScore.away ?? 0,
        } : null,
        status: EventStatus.fromOddsApi(sboMatch.status),
    };
}

export function getTournamentTeamImage(team?: ITeam | null): ImagePath {
    const name = IS_MATCH_INFO_SHOW_COUNTRY_FLAG ? team?.countryName : team?.teamName;
    return getTeamImage(flagRoundedImageGlob, name);
}

export function getTournamentPlayerImage(countryName?: string): ImagePath {
    return getTeamImage(footballPlayerImageGlob, countryName);
}

export enum KnockoutMatchStatus {
    Tbd = 'Tbd',
    Running = 'Running',
    Closed = 'Closed',
    End = 'End',
}

export function getKnockoutMatchStatus(match: IKnockoutMatch, runningMatches: ITournamentMatch[]): KnockoutMatchStatus {
    if (hasRunningMatch(match, runningMatches)) {
        return KnockoutMatchStatus.Running;
    }

    if (isMatchTbd(match)) {
        return KnockoutMatchStatus.Tbd;
    }

    if (hasIncompleteMatch(match)) {
        return KnockoutMatchStatus.Closed;
    }

    return KnockoutMatchStatus.End;
}

export function getKnockoutRunningMatch(match: IKnockoutMatch, runningMatches: ITournamentMatch[]): ITournamentMatch | null {
    const matchIds = [
        match.regularMatch?.id,
        match.extraTimeMatch?.id,
        match.penaltyMatch?.id,
    ].filter((id): id is number => id !== undefined && id !== null);

    return matchIds
        .map(matchId => runningMatches.find(match => match.eventId === matchId))
        .find(match => match !== undefined) ?? null;
}

function hasRunningMatch(match: IKnockoutMatch, runningMatches: ITournamentMatch[]): boolean {
    return getKnockoutRunningMatch(match, runningMatches) !== null;
}

function isMatchTbd(match: IKnockoutMatch): boolean {
    return !match.regularMatch;
}

function hasIncompleteMatch(match: IKnockoutMatch): boolean {
    const matchTypes = [
        match.regularMatch,
        match.extraTimeMatch,
        match.penaltyMatch,
    ];

    return matchTypes.some(matchType => matchType !== null && !isMatchCompleted(matchType));
}

function isMatchCompleted(match: ISboMatch): boolean {
    return match?.status === EventStatus.Completed;
}

export function getKnockoutCurrentStage(stageConfigByLeague: Map<number, Map<TournamentStage, Date>>, leagueId: number): TournamentStage {
    const leagueConfig = stageConfigByLeague.get(leagueId);
    if (!leagueConfig || leagueConfig.size === 0) {
        return TournamentStage.Unknown;
    }

    const stages = Array.from(leagueConfig.entries());
    const sortedStages = [...stages].sort((a, b) => a[1].getTime() - b[1].getTime());

    const now = new Date();
    if (now < sortedStages[0][1]) {
        return TournamentStage.Group;
    }

    const currentStage = sortedStages
        .filter(([, startDate]) => now >= startDate)
        .pop();

    if (!currentStage) {
        return TournamentStage.Group;
    }

    const stage = currentStage[0];
    if (stage === TournamentStage.ThirdPlace) {
        return TournamentStage.Final;
    }

    return stage;
}

function toNumber<T extends number>(value: string | undefined, defaultValue: T) {
    const num = Number(value);
    return Number.isNaN(num)
        ? defaultValue
        : num as T;
}
